import { useRef, type ReactElement } from 'react';
import { Button } from 'react-aria-components';
import { Icon } from '../ui/Icon';
import s from './PreviewBanner.module.css';

export interface PreviewBannerProps {
  /** The role combination being previewed ("Employee + Sector Lead (Maritime Airborne)"). */
  label: string;
  /** APP-122: acting as a real person (`label`), the tester who is really signed in. */
  signedInAs?: string;
  /** Opens Preview as to pick another combination; focus comes back to Change when it closes. */
  onChange?: (returnFocusTo: HTMLElement | null) => void;
  onExit: () => void;
}

/**
 * "Previewing as Employee + Sector Lead (Maritime Airborne) · Change · Exit preview" — sits above
 * the header while previewing (TEST1). Acting as a real person (APP-122, dev and test): "Acting as
 * Graham Whitlock · Sector Lead · Maritime Airborne", and who is really signed in.
 */
export function PreviewBanner({
  label,
  signedInAs,
  onChange,
  onExit,
}: PreviewBannerProps): ReactElement {
  const changeRef = useRef<HTMLButtonElement>(null);
  return (
    <section className={s.banner} aria-label="Preview">
      <div className={s.inner}>
        <p className={s.text}>
          <Icon name="eye" size={20} />
          {signedInAs === undefined ? (
            <strong>Previewing as {label}</strong>
          ) : (
            <>
              <strong>Acting as {label}</strong>
              <span className={s.note}>
                Signed in as {signedInAs}. What you do here is real, and the audit log records it
                with your name.
              </span>
            </>
          )}
        </p>
        <span className={s.actions}>
          {onChange && (
            <Button
              ref={changeRef}
              className={s.change}
              onPress={() => {
                onChange(changeRef.current);
              }}
            >
              Change
            </Button>
          )}
          <Button className={s.exit} onPress={onExit}>
            Exit preview
          </Button>
        </span>
      </div>
    </section>
  );
}
